import { Chip, Stack, Typography } from '@mui/material';

/**
 * SkillX students — GlobalTable headers.
 *
 * The column the screen exists for is "SkillX ID". It is blank for most rows
 * today, so it is rendered as a state rather than as an empty cell: an amber
 * "Not linked" pill reads as work to do, where a bare dash reads as missing
 * data nobody owns.
 *
 * Every renderCell reads `row`, never `value`. GlobalTable's getCellValue
 * substitutes an em dash for any falsy cell before renderCell sees it, so a
 * learner with no SkillX ID would otherwise be rendered inside a chip labelled
 * "—" instead of the "Not linked" state.
 *
 * One row is one student, not one enrolment — a learner on both the online and
 * offline course appears once, with both courses in the Courses cell.
 */

const fmtDate = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

export const skillxStudentsTableHead = [
  { field: 'sl_no', headerName: 'Sl No', width: 80 },
  {
    field: 'admn_no',
    headerName: 'Admission No',
    width: 130,
    renderCell: ({ row }) => (
      <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>{row.admn_no}</Typography>
    ),
  },
  { field: 'student_name', headerName: 'Student Name', width: 220, align: 'left' },
  { field: 'email', headerName: 'Email', width: 240, align: 'left' },
  { field: 'contact', headerName: 'Phone Number', width: 160 },
  {
    field: 'skillx_id',
    headerName: 'SkillX ID',
    width: 170,
    renderCell: ({ row }) =>
      row.skillx_id ? (
        <Chip
          size="small"
          label={row.skillx_id}
          sx={{ fontWeight: 700, fontSize: 12, color: '#2E75BC', bgcolor: '#E1EDFA' }}
        />
      ) : (
        <Chip
          size="small"
          label="Not linked"
          variant="outlined"
          sx={{ fontWeight: 600, fontSize: 12, color: '#9A6B00', borderColor: '#E3C67E' }}
        />
      ),
  },
  {
    // The button label is fixed for the whole column — GlobalTable takes it from
    // the header, not the row — so "Add" vs "Update" is carried by the SkillX ID
    // cell beside it rather than by the button.
    field: 'set_skillx_id',
    headerName: 'Action',
    type: 'action',
    actionType: 'edit',
    actionKey: 'SET_SKILLX_ID',
    buttonName: 'SkillX ID',
    icon: 'solar:pen-new-square-bold-duotone',
    width: 140,
  },
  {
    field: 'courses',
    headerName: 'SkillX Course(s)',
    width: 260,
    align: 'left',
    toolTipGetter: ({ row }) => row.courses || '',
  },
  {
    // Enrolment date is what the From/To filter above the grid acts on, so it is
    // on the row too — otherwise a filtered list has nothing to explain it.
    field: 'last_enrolled_on',
    headerName: 'Enrolled On',
    width: 150,
    renderCell: ({ row }) => (
      <Stack spacing={0.2} sx={{ py: 0.5 }}>
        <Typography sx={{ fontSize: 13.5, lineHeight: 1.3 }}>
          {fmtDate(row.last_enrolled_on) || '—'}
        </Typography>
        {row.enrollment_count > 1 && (
          <Typography sx={{ fontSize: 11.5, color: 'text.secondary', lineHeight: 1.3 }}>
            {row.enrollment_count} enrolments
          </Typography>
        )}
      </Stack>
    ),
  },
];
